import { Img } from "openimg/react";
import { useRef } from "react";
import { Link, Form } from "react-router";
import { getUserImgSrc } from "#app/utils/misc.tsx";
import { useUser, userHasRole, userHasPermission } from "#app/utils/user.ts";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";
import { Icon } from "./ui/icon";

export function UserDropdown() {
  const user = useUser();
  const formRef = useRef<HTMLFormElement>(null);
  const isAdmin =
    userHasRole(user, "admin") ||
    userHasPermission(user, "read:moderation:any");
  const userDisplayName = user.displayName ?? user.username;
  const avatarInitials = userDisplayName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button asChild variant="secondary">
          <Link
            to={`/users/${user.username}`}
            // this is for progressive enhancement
            onClick={(e) => e.preventDefault()}
            className="flex items-center gap-2"
          >
            {user.image?.objectKey ? (
              <Img
                className="size-8 rounded-full object-cover"
                alt={userDisplayName}
                src={getUserImgSrc(user.image.objectKey)}
                width={256}
                height={256}
              />
            ) : (
              <div className="size-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs">
                {avatarInitials}
              </div>
            )}
            <span className="text-body-sm font-bold">{userDisplayName}</span>
          </Link>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent sideOffset={8} align="end">
          <DropdownMenuItem asChild>
            <Link prefetch="intent" to={`/authors/${user.username}`}>
              <Icon className="text-body-md" name="avatar">
                Profile
              </Icon>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              prefetch="intent"
              to={`/authors/${user.username}/definitions`}
            >
              <Icon className="text-body-md" name="pencil-2">
                Definitions
              </Icon>
            </Link>
          </DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem asChild>
              <Link prefetch="intent" to="/admin">
                <Icon className="text-body-md" name="gear">
                  Admin
                </Icon>
              </Link>
            </DropdownMenuItem>
          )}
          <Form action="/logout" method="POST" ref={formRef}>
            <DropdownMenuItem asChild>
              <button type="submit" className="w-full">
                <Icon className="text-body-md" name="exit">
                  Logout
                </Icon>
              </button>
            </DropdownMenuItem>
          </Form>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
